import { Fragment, useEffect, useState } from "react";
import styled from "@xstyled/styled-components";

import {
  CompanyTeam,
  CompanyTeamMember,
  JobCompanyTeamMember,
  JobCompanyTeamMembersInput,
  TeamMemberRole,
} from "@toolbox/schema";
import { Checkbox } from "@toolbox/components/Input/Checkbox";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: sm;
`;

const TeamCheckbox = styled(Checkbox)`
  /* Prevents the whole line to be the label and thus clickable */
  display: inline-block;
`;

const Members = styled.div`
  display: grid;
  grid-template-columns: repeat(3, max-content);
  gap: xs sm;
  padding-left: sm;
`;

type PartialJobCompanyTeamMember = Omit<
  JobCompanyTeamMember,
  "__typename" | "id" | "prompts" | "jobCompanyTeamMemberPrompts"
> &
  Partial<Pick<JobCompanyTeamMember, "__typename" | "id">>;

const formatState = (
  teamId: string | undefined,
  members: JobCompanyTeamMember[]
) => ({
  currentTeamId: teamId,
  manager: members?.find(member => member.role === TeamMemberRole.Manager),
  directReports: members.filter(
    member => member.role === TeamMemberRole.Report
  ),
});

export const TeamSelect = ({
  teamId,
  teams,
  members,
  handleUpdate,
}: {
  teamId?: string;
  teams: CompanyTeam[];
  members: JobCompanyTeamMember[];
  handleUpdate: (
    teamId: string | undefined,
    teamMembers: JobCompanyTeamMembersInput[]
  ) => void;
}) => {
  const [{ currentTeamId, manager, directReports }, setState] = useState<{
    currentTeamId: string | undefined;
    manager: PartialJobCompanyTeamMember | undefined;
    directReports: PartialJobCompanyTeamMember[];
  }>(() => formatState(teamId, members));

  useEffect(() => {
    setState(formatState(teamId, members));
  }, [teamId, members]);

  const onChange = ({
    teamId,
    manager,
    directReports,
  }: {
    teamId: string | undefined;
    manager: PartialJobCompanyTeamMember | undefined;
    directReports: PartialJobCompanyTeamMember[];
  }) => {
    setState({ currentTeamId: teamId, manager, directReports });

    handleUpdate(teamId, [
      ...(manager
        ? [
            {
              companyTeamMemberId: manager.member.id,
              role: manager.role,
              id: manager.id,
            },
          ]
        : []),
      ...directReports.map(report => ({
        companyTeamMemberId: report.member.id,
        role: report.role,
        id: report.id,
      })),
    ]);
  };

  const handleChangeTeam = (teamId: string) => {
    const newTeamId = teamId === currentTeamId ? undefined : teamId;
    onChange({
      teamId: newTeamId,
      manager: undefined,
      directReports: [],
    });
  };

  const handleChangeManager = (member: CompanyTeamMember) => {
    const newManager =
      manager?.member.id === member.id
        ? undefined
        : {
            member,
            role: TeamMemberRole.Manager,
          };
    onChange({
      manager: newManager,
      teamId: currentTeamId,
      directReports,
    });
  };

  const handleChangeDirectReports = (member: CompanyTeamMember) => {
    const newDirectReports = directReports.some(
      report => report.member.id === member.id
    )
      ? directReports.filter(report => report.member.id !== member.id)
      : [
          ...directReports,
          {
            member,
            role: TeamMemberRole.Report,
          },
        ];
    onChange({
      directReports: newDirectReports,
      teamId: currentTeamId,
      manager,
    });
  };

  return (
    <Wrapper>
      {teams.map(team => {
        const isSelected = team.id === currentTeamId;

        return (
          <div key={team.id}>
            <TeamCheckbox
              checked={isSelected}
              onChange={() => handleChangeTeam(team.id)}
              label={team.name}
            />
            {isSelected && (
              <div>
                Members:
                <Members>
                  {team.members.map(member => {
                    const isManager = manager?.member.id === member.id;
                    const isDirectReport = directReports.some(
                      report => report.member.id === member.id
                    );

                    const isManagerDisabled =
                      isDirectReport || (!!manager && !isManager);
                    const isDirectReportDisabled =
                      isManager ||
                      (directReports.length > 1 && !isDirectReport);

                    return (
                      <Fragment key={member.id}>
                        {member.name && `${member.name}: `}
                        {member.jobTitle}

                        <Checkbox
                          checked={isManager}
                          disabled={isManagerDisabled}
                          onChange={() => handleChangeManager(member)}
                          label="Manager"
                        />
                        <Checkbox
                          checked={isDirectReport}
                          disabled={isDirectReportDisabled}
                          onChange={() => handleChangeDirectReports(member)}
                          label="Direct report"
                        />
                      </Fragment>
                    );
                  })}
                </Members>
              </div>
            )}
          </div>
        );
      })}
    </Wrapper>
  );
};
